import type { IdbObjectStoreIndex, IdbStoreRecord } from './idb-object-store';

export interface IdbStoreSchema<T extends IdbStoreRecord> {
  storeName: string;
  indexes: IdbObjectStoreIndex<T>[];
}

export interface IdbDatabaseConfig {
  name: string;
  version?: number;
}

type ResolveDatabase = (database: IDBDatabase) => void;
type RejectOpen = (reason?: unknown) => void;

/**
 * A wrapper around IndexedDB that manages database versioning, promisifying the API and object store schemas.
 * Stores must be registered before the database is opened. Once opened, the database connection is cached and shared across all stores using it. If a version change is detected, all connections are closed and the cache is cleared, allowing for a fresh connection with the new version and schema.
 */
export class IdbDatabase {
  private readonly name: string;
  private readonly version: number;
  private readonly stores = new Map<string, IdbStoreSchema<IdbStoreRecord>>();
  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor(config: IdbDatabaseConfig) {
    this.name = config.name;
    this.version = config.version ?? 1;
  }

  /**
   * Registers an object store schema with the database. This must be called before the database is opened, otherwise an error will be thrown. Once the database is opened, the schema is locked in and cannot be changed without triggering a version upgrade.
   * @param schema
   */
  registerStore<T extends IdbStoreRecord>(schema: IdbStoreSchema<T>): void {
    if (this.databasePromise) {
      throw new Error(
        `Cannot register store '${schema.storeName}' after database '${this.name}' has been opened`,
      );
    }

    this.stores.set(schema.storeName, schema as IdbStoreSchema<IdbStoreRecord>);
  }

  /**
   * Opens a connection to the IndexedDB database, creating or upgrading the database schema as needed. The connection is cached and shared across all stores using this database instance. If a version change is detected, all connections are closed and the cache is cleared, allowing for a fresh connection with the new version and schema.
   */
  open(): Promise<IDBDatabase> {
    if (this.databasePromise) {
      return this.databasePromise;
    }

    this.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.name, this.version);

      request.onupgradeneeded = () => this.runSchemaUpgrade(request);
      request.onsuccess = () => this.finalizeConnection(request, resolve);
      request.onerror = () => this.handleOpenError(request, reject);
      request.onblocked = () => this.handleOpenBlocked(reject);
    });

    return this.databasePromise;
  }

  /**
   * Handles the database schema upgrade process by creating or updating object stores and indexes based on the registered schemas. This method is called automatically by the IndexedDB API when a version change is detected during the open process.
   * @param request
   */
  private runSchemaUpgrade(request: IDBOpenDBRequest): void {
    const database = request.result;
    const transaction = request.transaction;

    if (transaction === null) {
      throw new Error(`IDB ${this.name} upgrade transaction missing`);
    }

    for (const schema of this.stores.values()) {
      let objectStore: IDBObjectStore;

      if (database.objectStoreNames.contains(schema.storeName)) {
        objectStore = transaction.objectStore(schema.storeName);
      }
      else {
        objectStore = database.createObjectStore(schema.storeName, { keyPath: 'key' });
      }

      for (const index of schema.indexes) {
        if (objectStore.indexNames.contains(index.keyPath)) {
          continue;
        }

        const unique = index.unique ?? false;

        objectStore.createIndex(index.keyPath, index.keyPath, { unique });
      }
    }
  }

  /**
   * Finalizes the database connection by setting up the version change handler and resolving the open promise. This method is called automatically by the IndexedDB API when the open process succeeds.
   * @param request
   * @param resolve
   */
  private finalizeConnection(request: IDBOpenDBRequest, resolve: ResolveDatabase): void {
    const database = request.result;

    database.onversionchange = () => {
      database.close();

      this.databasePromise = null;
    };

    resolve(database);
  }

  /**
   * Handles errors that occur during the database open process by clearing the cached promise and rejecting with the error. This method is called automatically by the IndexedDB API when the open process fails.
   * @param request
   * @param reject
   */
  private handleOpenError(request: IDBOpenDBRequest, reject: RejectOpen): void {
    this.databasePromise = null;

    reject(request.error);
  }

  /**
   * Handles the case where the database open process is blocked by another connection that has not closed yet. This can happen when multiple tabs or windows are trying to access the same database. The method clears the cached promise and rejects with an appropriate error message.
   * @param reject
   */
  private handleOpenBlocked(reject: RejectOpen): void {
    this.databasePromise = null;

    reject(new Error(`IDB ${this.name} open blocked by another connection`));
  }
}
